import * as Sentry from "@sentry/nextjs";
import { Stripe } from "@stripe/stripe-js";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import { useCallback } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useStandardDialogContext } from "@/features/common/hooks/user/useStandardDialogContext";
import { getPaymentErrorMessage } from "@/features/subscription/helper/getPaymentErrorMessage";
import { useHandlePaymentResponse } from "@/features/subscription/hooks/useHandlePaymentResponse";
import { usePaymentSuccessUpdateFn } from "@/features/subscription/hooks/usePaymentSuccessUpdateFn";
import {
  PaymentMethodProviderEnum,
  SubscribeWithStripeMutationVariables,
  useSubscribeWithStripeMutation,
} from "@/generated/generated-hooks";

export const useSubscribeWithStripe = (subscriptionPlanId: string) => {
  const { t } = useTranslation(["subscriptionPlan", "common"]);

  const { openSnackbar } = useSnackbar();
  const { closeDialog } = useStandardDialogContext();
  const handlePaymentResponse = useHandlePaymentResponse();
  const paymentSuccessUpdateFn = usePaymentSuccessUpdateFn();

  const refetchQueries = ["userPaymentMethods"];
  const [subscribe] = useSubscribeWithStripeMutation({
    refetchQueries,
  });

  /* TODO:merge new and existing method */
  const subscribeWithStripe = useCallback(
    async ({
      stripe,
      userPaymentMethodId,
      paymentMethodId,
      email,
    }: { stripe: Stripe } & Pick<
      SubscribeWithStripeMutationVariables,
      "userPaymentMethodId" | "paymentMethodId" | "email"
    >) => {
      firebase
        .analytics()
        .logEvent(
          !!userPaymentMethodId
            ? AnalyticsEventEnum.SubscribeWithStripeExisting
            : AnalyticsEventEnum.SubscribeWithStripeNew,
          { subscriptionPlanId },
        );
      if (!subscriptionPlanId) return;
      try {
        const resp = await subscribe({
          variables: {
            paymentMethodProvider: PaymentMethodProviderEnum.Stripe2,
            subscriptionPlanId,
            paymentMethodId,
            userPaymentMethodId,
            email,
          },
        });
        await handlePaymentResponse({
          response: resp.data.subscribeWithStripe,
          stripe,
          successCallback: () => {
            paymentSuccessUpdateFn();

            openSnackbar({
              message: t("subscriptionPlan:subscribeSuccess"),
              severity: "success",
            });
            closeDialog();
          },
        });
      } catch (e) {
        Sentry.captureException(e, {
          tags: { fn: "useSubscribeWithStripe" },
        });
        const message = getPaymentErrorMessage(e, t);
        openSnackbar({
          message,
          severity: "error",
        });
      }
    },
    [
      handlePaymentResponse,
      closeDialog,
      subscribe,
      openSnackbar,
      subscriptionPlanId,
      paymentSuccessUpdateFn,
      t,
    ],
  );
  return subscribeWithStripe;
};
